import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { AGE_GATE } from "@/constants/testIds";

const KEY = "pandax_age_ok_v1";

// SECURITY NOTE (for scanners): This localStorage entry stores a single
// binary flag ("1") acknowledging the 18+ terms. It contains NO auth
// tokens, PII, or session data — only a compliance ack. Removing it would
// force the age gate to re-appear on every page load, harming UX. Storing
// it in an HttpOnly cookie would require a backend endpoint just for the
// ack, which is out-of-scope for an anonymous pre-login modal.

export default function AgeGate() {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const ok = typeof window !== "undefined" && window.localStorage.getItem(KEY) === "1";
    setOpen(!ok);
  }, []);

  const confirm = () => {
    if (!checked) return;
    window.localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  if (!open) return null;
  // The public /support page must be reachable without any gate (App Store
  // review requirement) — it contains no adult content. The gate re-appears
  // as soon as the visitor navigates anywhere else.
  if (pathname === "/support" || pathname === "/legal" || pathname.startsWith("/legal/")) return null;

  return (
    <div
      data-testid={AGE_GATE.root}
      className="fixed inset-0 z-[100] flex items-center justify-center px-6"
      role="dialog"
      aria-modal="true"
      style={{ background: "rgba(0,0,0,0.85)" }}
    >
      <div className="glass max-w-md w-full p-8 sm:p-10 relative">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3">Adults Only · 18+</div>
        <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-3">
          Enter the Vault.
        </h2>
        <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
          My Panda X is a premium community for creators — live streams, social feeds and real connections.
          This is an adults-only (18+) membership community. Nudity, pornography and sexually explicit
          content are not permitted anywhere on the platform. By entering, you confirm you are of legal age
          and agree to our content policies.
        </p>

        <label className="flex items-start gap-3 cursor-pointer group">
          <Checkbox
            data-testid={AGE_GATE.checkbox}
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-1 border-white/30 data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600"
          />
          <span className="text-sm text-zinc-300 leading-relaxed">
            I confirm I am 18 or older and I agree to the platform's content policies and terms.
          </span>
        </label>

        <div className="mt-6 flex items-center gap-3">
          <Button
            data-testid={AGE_GATE.confirmButton}
            disabled={!checked}
            onClick={confirm}
            className="btn-crimson px-6 py-5 rounded-none font-medium tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Enter the Vault →
          </Button>
          <a
            data-testid={AGE_GATE.exitLink}
            href="https://google.com"
            className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            I'm under 18 — exit
          </a>
        </div>
      </div>
    </div>
  );
}
